import { useUIStore } from '@/config/stores/ui-store';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { messageListKeys, useActionStackStore } from '@/features/message-list';
import type { MessagesQueryParams } from '@/features/message-list';
import { dayjsLocal, uuidv4 } from '@/shared/utilities';
import { type InfiniteData, type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { categorizeAttachments } from '../components/message-input/message-input.sender';
import type { Attachment } from '../models/attachment';
import { Message } from '../models/message';
import { MessageContentType } from '../models/message-content-type';
import type { MessageSendReq } from '../models/message-send-dto';
import { sendMessageApi } from '../services/message.service';
import { useAttachmentUpload } from './use-attachment-upload';
import { MESSAGES_PAGE_SIZE } from './use-message-list';

/**
 * Custom hook for sending messages using React Query's useMutation.
 * @param conversationId - The ID of the conversation, can be null.
 * @returns An object containing the mutation function and its state (e.g., isLoading, error).
 */
export const useMessageSend = (conversationId: Nullable<string>) => {
  const { user } = useAuthStore();
  const { dispatchAction } = useActionStackStore();
  const queryClient = useQueryClient();
  const { mutateAsync } = useAttachmentUpload();

  const sendMessage = async (
    value: string,
    attachments: Attachment[],
    parentId: Nullable<string>
  ): Promise<Nullable<Message>> => {
    if (attachments.length > 0) {
      // Upload all attachments first
      const uploadedAttachments = await Promise.all(attachments.map((o) => mutateAsync(o)));
      const { medias, audios, files } = categorizeAttachments(uploadedAttachments);
      const content: Nullable<string> = value.trim();

      // Send medias in batches of 4
      if (medias.length > 0) {
        return await sendMessageApi(conversationId!, {
          contentType: MessageContentType.COMPOSE,
          parentMessageId: parentId || null,
          content: content,
          attachments: medias
        });
      }
      // Send audios
      if (audios.length > 0) {
        return await sendMessageApi(conversationId!, {
          contentType: MessageContentType.COMPOSE,
          parentMessageId: parentId || null,
          content: content,
          attachments: audios
        });
      }

      // Send files
      if (files.length > 0) {
        return await sendMessageApi(conversationId!, {
          contentType: MessageContentType.COMPOSE,
          parentMessageId: parentId || null,
          content: null,
          attachments: files
        });
      }
    }

    return await sendMessageApi(conversationId!, {
      contentType: MessageContentType.TEXT,
      content: value,
      parentMessageId: parentId || null
    });
  };

  return useMutation({
    mutationFn: async (messageData: MessageSendReq) => {
      const tempId = uuidv4();
      const preMsg = new Message({
        messageId: tempId,
        conversationId: conversationId!,
        contentType:
          messageData.attachments && messageData.attachments.length > 0
            ? MessageContentType.COMPOSE
            : MessageContentType.TEXT,
        senderId: user!.user_id,
        bucketId: '',
        content: messageData.content || '',
        structuredAttachments: messageData.attachments || null,
        isSending: true,
        sentAt: dayjsLocal(),
        parentMessageId: messageData.parentMessageId || null
      });

      // Attach parent message to temp message if replying
      if (messageData.parentMessageId) {
        const replyTarget = useUIStore.getState().getReplyMessage(conversationId!);
        if (replyTarget) {
          preMsg.parentMessage = replyTarget;
        }
      }
      await updateQueryData(queryClient, conversationId!, preMsg);
      dispatchAction({ type: 'scrollToBottom', conversationId: conversationId! });
      const res = await sendMessage(
        messageData.content || '',
        messageData.attachments || [],
        messageData.parentMessageId || null
      );
      if (messageData.parentMessageId && res) {
        const replyTarget = useUIStore.getState().getReplyMessage(conversationId!);
        if (replyTarget) {
          res.parentMessage = replyTarget;
        }
      }
      await updateQueryData(queryClient, conversationId!, res!, tempId);
      dispatchAction({ type: 'scrollToBottom', conversationId: conversationId! });
    }
  });
};

export const updateQueryData = async (
  queryClient: QueryClient,
  conversationId: string,
  msg: Message,
  tempId?: string
) => {
  queryClient.setQueryData<InfiniteData<Message[], MessagesQueryParams>>(
    messageListKeys.conversation(conversationId),
    (data) => {
      if (!data) {
        return {
          pages: [[msg]],
          pageParams: [{ fromMessageId: undefined, direction: 'OLDER', pageSize: MESSAGES_PAGE_SIZE }]
        };
      }

      // Check if the actual messageId already exists
      const messageExists = data.pages.some((page) =>
        page.some((existingMsg) => existingMsg.messageId === msg.messageId)
      );

      if (messageExists) {
        return data;
      }

      // Create shallow copy of all pages to avoid mutation
      const updatedPages = data.pages.map((page) => [...page]);

      let foundTemp = false;
      if (tempId) {
        // Find and replace temporary message if tempId is provided
        for (let pageIndex = 0; pageIndex < updatedPages.length; pageIndex++) {
          const page = updatedPages[pageIndex];
          const msgIndex = page.findIndex((existingMsg) => existingMsg.messageId === tempId);
          if (msgIndex !== -1) {
            const existingMsg = updatedPages[pageIndex][msgIndex];
            // Preserve parentMessage from the message being replaced
            const replacedMsg = new Message({ ...msg, isSending: false });

            // Prioritize msg.parentMessage (if passed), then existingMsg.parentMessage
            replacedMsg.parentMessage = msg.parentMessage || existingMsg.parentMessage;

            updatedPages[pageIndex][msgIndex] = replacedMsg;
            foundTemp = true;
            break;
          }
        }
      }

      // Attach parent message if exists and not already attached
      if (msg.parentMessageId && !msg.parentMessage) {
        let parentMsg: Message | undefined;
        for (const page of data.pages) {
          parentMsg = page.find((m) => m.messageId === msg.parentMessageId);
          if (parentMsg) break;
        }
        if (parentMsg) {
          msg.parentMessage = parentMsg;
        }
      }

      // Add new message if temp was not found
      if (!foundTemp) {
        // New messages should be added to the last page (newest messages)
        const lastPageIndex = updatedPages.length - 1;
        updatedPages[lastPageIndex] = [...updatedPages[lastPageIndex], msg];
      }

      return {
        pages: updatedPages,
        pageParams: data.pageParams
      };
    }
  );
};
